import React, { useEffect, useMemo, useState } from 'react'
import SettingsEmptyState from '../../components/settings/SettingsEmptyState';
import { useTranslation } from 'react-i18next'
import { IoAddCircleOutline } from 'react-icons/io5'
import { MwButton, MwSpinnerButton, MwToast } from '../../components/ui'
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal'
import ActionsDropdown from '../../components/common/ActionsDropdown'
import ActiveIndicator from '../../components/common/ActiveIndicator'
import { useCurrent, useDelete, useFetch, usePost, usePut } from '../../hooks'

const toBool = (value) => value === true || value === 'true' || value === 1 || value === '1'

const normalizeDetails = (details) => {
  if (!Array.isArray(details)) return []
  return details.map((d) => ({
    id: d?.id ?? null,
    name: d?.name?.trim?.() ?? '',
    short_name: d?.short_name?.trim?.() ?? '',
    rate: d?.rate?.toString?.() ?? ''
  }))
}

const emptyForm = (tenantId) => ({
  template_id: null,
  uom_master_id: null,
  template_name: '',
  template_is_active: true,
  uom_master_name: '',
  uom_master_short_name: '',
  uom_details: [{ id: -1, name: '', short_name: '', rate: '' }],
  tenant_id: tenantId
})

const UomModal = ({ isOpen, onClose, mode, templateId, onSuccess }) => {
  const { t } = useTranslation('global')
  const { currentTenantId } = useCurrent()
  const { postData, loading: postLoading, error: postError, data: postRes } = usePost(true)
  const { putData, loading: putLoading, error: putError, data: putRes } = usePut()
  const { deleteItem, loading: deleteLoading } = useDelete()

  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  const isAdd = mode === 'add'
  const isLoading = postLoading || putLoading

  const shouldFetchDetails = (isEdit || isView) && templateId
  const detailUrl = shouldFetchDetails ? `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom/${templateId}` : null
  const { data: detailsData, loading: rawDetailsLoading } = useFetch(detailUrl || '', shouldFetchDetails)
  const detailsLoading = shouldFetchDetails ? rawDetailsLoading : false

  const [form, setForm] = useState(() => emptyForm(currentTenantId))
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  useEffect(() => {
    setForm(emptyForm(currentTenantId))
  }, [currentTenantId])

  useEffect(() => {
    if (!shouldFetchDetails) return
    if (!detailsData?.data) return

    const template = detailsData.data
    const uomMaster = template?.uom_master

    setForm({
      template_id: templateId,
      uom_master_id: uomMaster?.id ?? null,
      template_name: template?.name?.trim?.() ?? '',
      template_is_active: toBool(template?.is_active),
      uom_master_name: uomMaster?.name?.trim?.() ?? '',
      uom_master_short_name: uomMaster?.short_name?.trim?.() ?? '',
      uom_details: normalizeDetails(uomMaster?.uom_details).length ? normalizeDetails(uomMaster?.uom_details) : [{ id: -1, name: '', short_name: '', rate: '' }],
      tenant_id: template?.tenant_id ?? currentTenantId
    })
  }, [shouldFetchDetails, detailsData, templateId, currentTenantId])

  useEffect(() => {
    if (postRes?.status === 1) {
      setToastMessage(t('uom.saveSuccess'))
      setShowToast(true)
      onSuccess?.()
      onClose?.()
    }
  }, [postRes, onSuccess, onClose, t])

  useEffect(() => {
    if (putRes?.status === 1) {
      setToastMessage(t('uom.updateSuccess'))
      setShowToast(true)
      onSuccess?.()
      onClose?.()
    }
  }, [putRes, onSuccess, onClose, t])

  useEffect(() => {
    if (postError || putError) {
      setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'))
      setShowToast(true)
    }
  }, [postError, putError, t])

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }))

  const addDetail = () => {
    setForm((prev) => {
      const minId = prev.uom_details.reduce((min, d) => (typeof d.id === 'number' && d.id < min ? d.id : min), 0)
      const nextId = minId <= 0 ? minId - 1 : -1
      return { ...prev, uom_details: [...prev.uom_details, { id: nextId, name: '', short_name: '', rate: '' }] }
    })
  }

  const updateDetail = (id, patch) => {
    setForm((prev) => ({
      ...prev,
      uom_details: prev.uom_details.map((d) => (d.id === id ? { ...d, ...patch } : d))
    }))
  }

  const removeDetail = async (id) => {
    const target = form.uom_details.find((d) => d.id === id)
    if (!target) return

    if (isEdit && typeof id === 'number' && id > 0) {
      try {
        await deleteItem(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom/details/${id}`)
      } catch (e) {
        setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'))
        setShowToast(true)
        return
      }
    }

    setForm((prev) => ({ ...prev, uom_details: prev.uom_details.filter((d) => d.id !== id) }))
  }

  const canSubmit = useMemo(() => {
    if (isView) return false
    if (!form.template_name.trim()) return false
    if (!form.uom_master_name.trim()) return false
    if (!form.uom_master_short_name.trim()) return false
    if (!form.uom_details.length) return false
    return form.uom_details.every((d) => d.name.trim() && d.short_name.trim() && d.rate !== '' && !Number.isNaN(Number(d.rate)))
  }, [form, isView])

  const submit = async () => {
    if (!canSubmit) return

    if (isAdd) {
      const payload = {
        template_name: form.template_name,
        template_is_active: form.template_is_active ? 1 : 0,
        uom_master_name: form.uom_master_name,
        uom_master_short_name: form.uom_master_short_name,
        uom_details: form.uom_details.map((d) => ({
          uom_details_name: d.name,
          uom_details_short_name: d.short_name,
          uom_details_rate: d.rate.toString()
        })),
        tenant_id: currentTenantId
      }
      await postData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom`, payload)
      return
    }

    const payload = {
      template_id: Number(templateId),
      uom_master_id: form.uom_master_id,
      template_name: form.template_name,
      template_is_active: form.template_is_active ? 1 : 0,
      uom_master_name: form.uom_master_name,
      uom_master_short_name: form.uom_master_short_name,
      uom_details: form.uom_details.map((d) => ({
        uom_details_id: d.id > 0 ? d.id : null,
        uom_details_name: d.name,
        uom_details_short_name: d.short_name,
        uom_details_rate: d.rate.toString()
      })),
      tenant_id: currentTenantId
    }

    await putData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom`, payload)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-800 bg-opacity-50 flex justify-center items-center text-xs">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />
      <div className="bg-white w-full max-w-3xl mx-auto rounded-lg shadow-lg p-6 relative">
        {(detailsLoading || isLoading) && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col justify-center items-center rounded-lg z-10">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin"></div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-gray-500 text-base">
            {isAdd ? t('uom.addNew') : isEdit ? t('uom.edit') : t('uom.view')}
          </h2>
          <button
            onClick={onClose}
            type="button"
            className="text-gray-500 hover:text-gray-700"
            disabled={detailsLoading || isLoading || deleteLoading}
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-500 text-sm mb-2">{t('uom.templateName')}</label>
            <input
              type="text"
              value={form.template_name}
              onChange={(e) => updateForm({ template_name: e.target.value })}
              className="w-full p-2 border rounded bg-white"
              disabled={isView || detailsLoading || isLoading}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.template_is_active}
                onChange={(e) => updateForm({ template_is_active: e.target.checked })}
                className="mr-2"
                disabled={isView || detailsLoading || isLoading}
              />
              <span className="text-gray-500 text-sm">{t('common.active', 'نشط')}</span>
            </label>
          </div>

          <div>
            <label className="block text-gray-500 text-sm mb-2">{t('uom.baseUnitName')}</label>
            <input
              type="text"
              value={form.uom_master_name}
              onChange={(e) => updateForm({ uom_master_name: e.target.value })}
              className="w-full p-2 border rounded bg-white"
              disabled={isView || detailsLoading || isLoading}
            />
          </div>
          <div>
            <label className="block text-gray-500 text-sm mb-2">{t('uom.baseUnitShortName')}</label>
            <input
              type="text"
              value={form.uom_master_short_name}
              onChange={(e) => updateForm({ uom_master_short_name: e.target.value })}
              className="w-full p-2 border rounded bg-white"
              disabled={isView || detailsLoading || isLoading}
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <div className="text-gray-500 font-semibold">{t('uom.details')}</div>
            {!isView && (
              <MwButton inGroup={true} type="addBtn" onClick={addDetail} disabled={detailsLoading || isLoading}>
                <IoAddCircleOutline />
                <span>{t('uom.addDetail')}</span>
              </MwButton>
            )}
          </div>

          <div className="w-full text-xs rounded-xl bg-slate-50 text-gray-500">
            <div className="grid grid-cols-12 gap-2 font-bold px-3 py-2 rounded-t-xl items-center bg-slate-100">
              <div className="col-span-5">{t('uom.detailName')}</div>
              <div className="col-span-3">{t('uom.detailShortName')}</div>
              <div className="col-span-3">{t('uom.detailRate')}</div>
              <div className="col-span-1"></div>
            </div>

            {form.uom_details.map((d) => (
              <div key={d.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t items-center hover:bg-slate-50">
                <div className="col-span-5">
                  <input
                    type="text"
                    value={d.name}
                    onChange={(e) => updateDetail(d.id, { name: e.target.value })}
                    className="w-full p-2 border rounded bg-white"
                    disabled={isView || detailsLoading || isLoading}
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="text"
                    value={d.short_name}
                    onChange={(e) => updateDetail(d.id, { short_name: e.target.value })}
                    className="w-full p-2 border rounded bg-white"
                    disabled={isView || detailsLoading || isLoading}
                  />
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={d.rate}
                    onChange={(e) => updateDetail(d.id, { rate: e.target.value })}
                    className="w-full p-2 border rounded bg-white"
                    disabled={isView || detailsLoading || isLoading}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  {!isView && (
                    <button
                      type="button"
                      className="text-red-500 px-2 py-1 hover:bg-red-50 rounded"
                      onClick={() => removeDetail(d.id)}
                      disabled={detailsLoading || isLoading || deleteLoading}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <MwButton
            type="cancelBtn"
            size="sm"
            onClick={onClose}
            disabled={detailsLoading || isLoading || deleteLoading}
          >
            {t('common.cancel')}
          </MwButton>
          {!isView && (
            <MwButton
              type="bottomBtn"
              size="sm"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              onClick={submit}
              disabled={!canSubmit || detailsLoading || isLoading || deleteLoading}
            >
              {isAdd ? t('common.add', 'إضافة') : t('common.save')}
            </MwButton>
          )}
        </div>
      </div>
    </div>
  )
}

const UomSettings = () => {
  const { t, i18n } = useTranslation('global')
  const isRTL = i18n.language === 'ar'
  const lang = i18n.language

  const { currentTenantId } = useCurrent()
  const listUrl = currentTenantId ? `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom/tenant/${currentTenantId}` : null
  const { data, loading, refreshHandler } = useFetch(listUrl, !!listUrl)
  const { deleteItem, loading: deleteLoading } = useDelete()

  const units = Array.isArray(data?.data) ? data.data : []

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  const openAdd = () => {
    setSelectedTemplateId(null)
    setModalMode('add')
    setModalOpen(true)
  }

  const openView = (id) => {
    setSelectedTemplateId(id)
    setModalMode('view')
    setModalOpen(true)
  }

  const openEdit = (id) => {
    setSelectedTemplateId(id)
    setModalMode('edit')
    setModalOpen(true)
  }

  const openDelete = (id) => {
    setSelectedTemplateId(id)
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    try {
      await deleteItem(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/inventory/uom/${selectedTemplateId}`)
      setToastMessage(t('uom.deleteSuccess'))
      setShowToast(true)
      refreshHandler()
    } catch (e) {
      setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'))
      setShowToast(true)
    } finally {
      setDeleteModalOpen(false)
      setSelectedTemplateId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <MwSpinnerButton withLabel={false} />
      </div>
    )
  }

  if (!units.length) {
    return (
      <div className="bg-gray-50 p-6 rounded-lg text-center">
        <SettingsEmptyState msg={t('uom.noData')} />
        <div className="mt-3 flex justify-center">
          <MwButton inGroup={true} type="saveBtn" onClick={openAdd}>
            <IoAddCircleOutline />
            <span>{t('uom.add')}</span>
          </MwButton>
        </div>
      </div>
    )
  }

  return (
    <div className="text-gray-500">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />

      <div className="flex justify-between items-center mb-4">
        <p className="font-bold text-sm">{t('uom.title')}</p>
        <MwButton
          onClick={openAdd}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          type="saveBtn"
        >
          <IoAddCircleOutline size={16} />
          {t('uom.add')}
        </MwButton>
      </div>

      <div className="w-full text-xs rounded-xl">
        <div className="flex gap-2 font-bold px-3 py-2 rounded-t-xl justify-between items-center">
          <div className="w-16 text-center">#</div>
          <div className="flex-1">{t('uom.templateName')}</div>
          <div className="flex-1">{t('uom.baseUnitName')}</div>
          <div className="w-36 text-center">{t('uom.status')}</div>
          <div className="w-36 text-center">{t('uom.actions')}</div>
        </div>

        {units.map((unit, index) => {
          const templateName = lang === 'ar' ? unit?.name_ar?.trim?.() : unit?.name?.trim?.()
          const resolvedName = templateName || unit?.name?.trim?.() || unit?.name_ar?.trim?.() || ''
          return (
            <div key={unit.id} className="flex gap-2 px-3 py-1 border-t justify-between items-center text-xs">
              <div className="w-16 text-center">{index + 1}</div>
              <div className="flex-1">{resolvedName}</div>
              <div className="flex-1">{unit?.uom_master?.name || ''}</div>
              <div className="w-36 text-center">
                <div className="w-36 flex justify-center items-center">
                  <ActiveIndicator active={toBool(unit?.is_active)} />
                </div>
              </div>
              <div className="w-36 text-center">
                <ActionsDropdown
                  isRTL={isRTL}
                  onView={() => openView(unit.id)}
                  onEdit={() => openEdit(unit.id)}
                  onDelete={() => openDelete(unit.id)}
                  itemId={unit.id}
                  viewText={t('uom.view')}
                  editText={t('common.edit')}
                  deleteText={t('common.delete')}
                />
              </div>
            </div>
          )
        })}
      </div>

      {modalOpen && (
        <UomModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          mode={modalMode}
          templateId={selectedTemplateId}
          onSuccess={refreshHandler}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          title={t('uom.deleteConfirmTitle')}
          message={t('uom.deleteConfirmMessage')}
          loading={deleteLoading}
        />
      )}
    </div>
  )
}

export default UomSettings
